import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import { orientedFace, planPoint, pyramidOverRing } from '../../solids';
import { rectWall, roofUv, slopedWall } from '../masonry';
import { fittedButtress } from '../oudeKerk/oudeKerkButtress';
import type { RadiatingChapel } from './nieuweKerkChapels';
import { bayWindows, lancet, LIGHTS, type NieuweKerkBuild } from './nieuweKerkParts';

/**
 * The ambulatory round the high choir under a lean-to roof that follows the apse, and the radiating
 * chapels on its outer sides, each with a window in every outer wall and its own pointed roof, as the
 * painting (nieuwekerk-scan, frame x 1061-1100, rows 571-603) and the woodcut (nieuwekerk-woodcut)
 * draw them, on the floor plan's walls (nieuweKerkChapels.ts). Pinnacled buttresses stand where the
 * chapels meet. The chapels are brick banded with stone; the ambulatory, of the east end before 1452,
 * is of tuff (De Gids 1942, p. 31).
 */
const BUTTRESS = { width: 0.8, depth: 1.4, rise: 0.6, pinnacle: 2.4 } as const;
const CHAPEL_LIGHT = { share: 0.45, max: 1.8 } as const;

/** Windows in the stretch of the ambulatory's straight walls east of the chapels beside the choir. */
function straightWindows(s: NieuweKerkBuild, from: Vec2, to: Vec2): Ring[] {
  const reach = s.plan.choirChapels.reduce((m, c) => Math.max(m, c.u1), -Infinity);
  const bays = s.plan.choirBays;
  const east = to[0] > from[0];
  return bayWindows(bays, from[0], east ? 1 : -1, LIGHTS.aisle, (u0, u1) => (u0 + u1) / 2 < reach);
}

/** The ambulatory's outer walls where no chapel opens off it: the chapels stand open to it, as the plan draws them. */
function buildOuterWalls(s: NieuweKerkBuild): void {
  const { outer, eaveY } = s.plan.ambulatory;
  const chapels = new Set(s.plan.radiatingChapels.map((c) => c.facet));
  outer.slice(1).forEach((p, i) => {
    const q = outer[i]!;
    const facet = i - 1;
    if (chapels.has(facet)) return;
    const straight = i === 0 || i === outer.length - 2;
    const length = Math.hypot(p[0] - q[0], p[1] - q[1]);
    const openings = straight ? straightWindows(s, q, p) : [lancet(length / 2, LIGHTS.aisle)];
    rectWall(s.builder, s.stone, s.frame, [q, p], [0, eaveY], openings);
  });
}

/** The inner walls against the clerestory, the two ends at the transept and the roof between. */
function buildInnerAndRoof(s: NieuweKerkBuild): void {
  const { inner, outer, eaveY, topY } = s.plan.ambulatory;
  const last = inner.length - 1;
  inner
    .slice(1)
    .forEach((p, i) => { rectWall(s.builder, s.stone, s.frame, [p, inner[i]!], [0, topY]); });
  slopedWall(s.builder, s.stone, s.frame, [outer[last]!, inner[last]!], eaveY, topY);
  slopedWall(s.builder, s.stone, s.frame, [inner[0]!, outer[0]!], topY, eaveY);
  outer.slice(1).forEach((p, i) => {
    const q = outer[i]!;
    const points: Vec3[] = [
      planPoint(q[0], eaveY, q[1]),
      planPoint(p[0], eaveY, p[1]),
      planPoint(inner[i + 1]![0], topY, inner[i + 1]![1]),
      planPoint(inner[i]![0], topY, inner[i]![1]),
    ];
    orientedFace(
      s.builder,
      s.stone.roof,
      s.frame,
      points,
      [p[1] - q[1], 1, p[0] - q[0]],
      roofUv(points[0]!, points[1]!, points[3]!)
    );
  });
}

/**
 * A chapel's walls: a window in each of its outer walls, its side walls only where no chapel stands
 * beside it, and none toward the ambulatory, which it opens into.
 */
function buildRadiatingChapel(s: NieuweKerkBuild, c: RadiatingChapel, chapels: ReadonlySet<number>): void {
  const last = c.ring.length - 1;
  c.ring.slice(0, last).forEach((p, i) => {
    const q = c.ring[i + 1]!;
    const side = i === 0 || i === last - 1;
    const shared = (i === 0 && chapels.has(c.facet - 1)) || (i === last - 1 && chapels.has(c.facet + 1));
    if (shared) return;
    const length = Math.hypot(q[0] - p[0], q[1] - p[1]);
    const width = Math.min(CHAPEL_LIGHT.max, CHAPEL_LIGHT.share * length);
    rectWall(s.builder, s.brick, s.frame, [p, q], [0, c.eaveY], side ? [] : [lancet(length / 2, { ...LIGHTS.chapel, width })]);
  });
  pyramidOverRing(s.builder, { roof: s.stone.roof, wall: s.stone.wall }, s.frame, c.ring, c.eaveY, { plan: c.apex, y: c.apexY });
}

function outwardOf(a: Vec2, b: Vec2): Vec2 {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [(b[1] - a[1]) / length, (a[0] - b[0]) / length];
}

const same = (a: Vec2, b: Vec2): boolean => Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-9;

/** The chevet's outside walked counter-clockwise: the ambulatory's outer wall with the chapels' walls in place of their sides. */
function chevetOutline(outer: readonly Vec2[], chapels: readonly RadiatingChapel[]): Vec2[] {
  const walk: Vec2[] = [outer[0]!, outer[1]!];
  for (let facet = 0; facet < outer.length - 3; facet++) {
    const chapel = chapels.find((c) => c.facet === facet);
    walk.push(...(chapel ? chapel.ring.slice(1) : [outer[2 + facet]!]));
  }
  walk.push(outer[outer.length - 1]!);
  return walk.reduce<Vec2[]>((line, p) => {
    if (line.length >= 2 && same(line[line.length - 2]!, p)) return line.slice(0, -1);
    return line.length > 0 && same(line[line.length - 1]!, p) ? line : [...line, p];
  }, []);
}

/**
 * A pinnacled buttress where the chapels' side walls meet their outer walls, and at the corner of the
 * ambulatory's straight south wall, along the outline's bisector.
 */
function buildChevetButtresses(s: NieuweKerkBuild): void {
  const { outer, eaveY } = s.plan.ambulatory;
  const chapels = s.plan.radiatingChapels;
  const spec = { width: BUTTRESS.width, depth: BUTTRESS.depth, height: eaveY + BUTTRESS.rise, pinnacle: BUTTRESS.pinnacle };
  const sites = [outer[1]!, ...chapels.flatMap((c) => [c.ring[1]!, c.ring[c.ring.length - 2]!])];
  const outline = chevetOutline(outer, chapels);
  outline.slice(1, -1).forEach((p, k) => {
    if (!sites.some((q) => same(p, q))) return;
    const before = outwardOf(outline[k]!, p);
    const after = outwardOf(p, outline[k + 2]!);
    const length = Math.hypot(before[0] + after[0], before[1] + after[1]);
    fittedButtress(s, p, [(before[0] + after[0]) / length, (before[1] + after[1]) / length], spec);
  });
}

export function buildAmbulatory(s: NieuweKerkBuild): void {
  buildOuterWalls(s);
  buildInnerAndRoof(s);
  const chapels = new Set(s.plan.radiatingChapels.map((c) => c.facet));
  for (const c of s.plan.radiatingChapels) buildRadiatingChapel(s, c, chapels);
  buildChevetButtresses(s);
}
